import { Dimensions, useWindowDimensions } from "react-native";
import { useHeaderHeight } from "@react-navigation/elements";
import { useBottomTabBarHeight } from "@react-navigation/bottom-tabs";
import {
  useSafeAreaFrame,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

import {
  type DiagnosticItem,
  DiagnosticStatus,
} from "@/schemas/app/diagnostic";

/**
 * Safely gets header height, returning 0 if not in a navigation context
 */
function useSafeHeaderHeight(): number {
  try {
    return useHeaderHeight();
  } catch (error) {
    return 0;
  }
}

/**
 * Safely gets tab bar height, returning 0 if not in a navigation context
 */
function useSafeTabBarHeight(): number {
  try {
    return useBottomTabBarHeight();
  } catch (error) {
    return 0;
  }
}

export function useLayoutDiagnostics(): DiagnosticItem[] {
  const headerHeight = useSafeHeaderHeight();
  const tabBarHeight = useSafeTabBarHeight();

  // Here you can safely use React hooks
  const { height: safeHeight } = useSafeAreaFrame();
  const insets = useSafeAreaInsets();
  const { height } = useWindowDimensions();

  const availableHeight = height - (headerHeight + tabBarHeight);

  // Create additional diagnostics that use the hook values
  const hookBasedDiagnostics: DiagnosticItem[] = [
    {
      id: "headerHeight",
      name: "Header Height",
      value: `${headerHeight}`,
      status: headerHeight > 0 ? DiagnosticStatus.OK : DiagnosticStatus.WARNING,
    },
    {
      id: "safeHeight",
      name: "Safe Area Height",
      value: `${safeHeight}`,
      status: safeHeight > 0 ? DiagnosticStatus.OK : DiagnosticStatus.WARNING,
    },
    {
      id: "tabBarHeight",
      name: "Tab Bar Height",
      value: `${tabBarHeight}`,
      status: tabBarHeight > 0 ? DiagnosticStatus.OK : DiagnosticStatus.WARNING,
    },
    {
      id: "availableHeight",
      name: "Available Height",
      value: `${availableHeight}`,
      status:
        availableHeight > 0 ? DiagnosticStatus.OK : DiagnosticStatus.WARNING,
    },
    {
      id: "insets",
      name: "Safe Area Insets",
      value: JSON.stringify(insets),
      status: insets.top > 0 ? DiagnosticStatus.OK : DiagnosticStatus.WARNING,
    },
  ];

  // Get the base diagnostics that don't need hooks
  const baseDiagnostics = fetchLayoutDiagnostics();

  // Return all diagnostics combined
  const results = [...baseDiagnostics, ...hookBasedDiagnostics];
  return results;
}

export function fetchLayoutDiagnostics(): DiagnosticItem[] {
  // Get screen dimensions
  const { width: windowWidth, height: windowHeight } = Dimensions.get("window");
  const { width: screenWidth, height: screenHeight } = Dimensions.get("screen");

  return [
    {
      id: "windowLayout",
      name: "Window Width x Height",
      value: `${windowWidth} x ${windowHeight}`,
      status:
        windowWidth > 0 && windowWidth > 0
          ? DiagnosticStatus.OK
          : DiagnosticStatus.WARNING,
    },
    {
      id: "screenLayout",
      name: "Screen Width x Height",
      value: `${screenWidth} x ${screenHeight}`,
      status:
        screenWidth > 0 && screenHeight > 0
          ? DiagnosticStatus.OK
          : DiagnosticStatus.WARNING,
    },
  ];
}
